// S14-4: VISTA Admin Console — Releases page.
//
// Lists releases recorded by the admin worker (D1 `releases`), grouped
// by channel (stable / rc / dev). Each release card surfaces:
//   - version + released_at + notes summary
//   - per-artifact upload state (manifest claims vs. R2 reality)
//   - download buttons that hit /vista-admin/releases/:v/artifacts/:n
//   - delete button on dev channel only (rc/stable are append-only)
//
// Auth is handled upstream by AuthGate + the api client; this page just
// uses the admin api.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const CHANNELS = [
  { key: 'all', label: 'All', tone: 'gray' },
  { key: 'stable', label: 'Stable', tone: 'emerald' },
  { key: 'rc', label: 'Release candidates', tone: 'amber' },
  { key: 'dev', label: 'Dev', tone: 'sky' },
];

function ChannelPill({ channel }) {
  const tone =
    channel === 'stable'
      ? 'bg-emerald-100 text-emerald-700'
      : channel === 'rc'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-sky-100 text-sky-700';
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{channel}</span>
  );
}

function formatBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatTime(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function ReleaseCard({ release, onSelect, selected }) {
  const channel = release.channel;
  const versionEsc = encodeURIComponent(release.version);
  const baseDownloadUrl = (artifact) =>
    `/api/v1/vista-admin/releases/${versionEsc}/artifacts/${encodeURIComponent(artifact)}?channel=${channel}`;

  return (
    <button
      type="button"
      onClick={() => onSelect(release)}
      className={`block w-full rounded-lg border bg-white p-4 text-left shadow-sm transition-colors hover:border-blue-300 ${
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-base font-semibold text-gray-900">{release.version}</span>
          <ChannelPill channel={channel} />
        </div>
        <span className="text-xs text-gray-500">{formatTime(release.released_at)}</span>
      </div>
      {release.notes && (
        <p className="mt-1.5 line-clamp-2 text-xs text-gray-600">{release.notes}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
        {release.sha256_agent && (
          <span title={`agent sha256: ${release.sha256_agent}`}>
            agent: <span className="font-mono">{release.sha256_agent.slice(0, 12)}…</span>
          </span>
        )}
        {release.sha256_proxy && (
          <span title={`proxy sha256: ${release.sha256_proxy}`}>
            proxy: <span className="font-mono">{release.sha256_proxy.slice(0, 12)}…</span>
          </span>
        )}
      </div>
      {/* baseDownloadUrl reserved for the detail panel; unused at the
          card level but kept so the card and detail share the URL shape. */}
      <span className="sr-only">{baseDownloadUrl('manifest.json')}</span>
    </button>
  );
}

function ArtifactRow({ release, artifact }) {
  const versionEsc = encodeURIComponent(release.version);
  const nameEsc = encodeURIComponent(artifact.name);
  const url = `/api/v1/vista-admin/releases/${versionEsc}/artifacts/${nameEsc}?channel=${release.channel}`;
  return (
    <li className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-mono text-sm text-gray-900">{artifact.name}</span>
        <span className="font-mono text-xs text-gray-500">
          {artifact.sha256?.slice(0, 16)}…  ·  {formatBytes(artifact.size ?? artifact.uploaded_size)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {artifact.uploaded ? (
          <a
            href={url}
            download={artifact.name}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
          >
            Download
          </a>
        ) : (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
            pending upload
          </span>
        )}
      </div>
    </li>
  );
}

function ReleaseDetail({ release, onDelete, deleting }) {
  const versionEsc = encodeURIComponent(release.version);
  const { data: detail, isLoading } = useQuery({
    queryKey: ['release', release.channel, release.version],
    queryFn: () => api.get(`/vista-admin/releases/${versionEsc}`, { channel: release.channel }),
    select: (r) => r?.data ?? r,
    enabled: !!release,
  });

  return (
    <aside className="flex h-full flex-col gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-mono text-lg font-bold text-gray-900">{release.version}</h2>
            <ChannelPill channel={release.channel} />
          </div>
          {release.channel === 'dev' && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">{formatTime(release.released_at)}</p>
      </header>
      {release.notes && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Release notes</h3>
          <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{release.notes}</p>
        </section>
      )}
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Artifacts</h3>
        {isLoading ? (
          <p className="mt-2 text-sm text-gray-500">Loading…</p>
        ) : detail?.artifacts && detail.artifacts.length > 0 ? (
          <ul className="mt-2 space-y-1.5">
            {detail.artifacts.map((a) => (
              <ArtifactRow key={a.name} release={release} artifact={a} />
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-gray-500">No artifacts in manifest.</p>
        )}
      </section>
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">R2 prefix</h3>
        <p className="mt-1 truncate font-mono text-xs text-gray-600">{release.r2_key_prefix}</p>
      </section>
    </aside>
  );
}

export default function Releases() {
  const [channelFilter, setChannelFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const queryClient = useQueryClient();

  const { data: releases, isLoading, error } = useQuery({
    queryKey: ['releases', channelFilter],
    queryFn: () =>
      api.get(
        '/vista-admin/releases',
        channelFilter === 'all' ? undefined : { channel: channelFilter },
      ),
    select: (r) => r?.data ?? [],
  });

  const deleteMutation = useMutation({
    mutationFn: (release) =>
      api.delete(`/vista-admin/releases/${encodeURIComponent(release.version)}`, null, {
        channel: release.channel,
      }),
    onSuccess: () => {
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
  });

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Releases</h1>
        <nav className="flex items-center gap-1.5">
          {CHANNELS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => {
                setChannelFilter(c.key);
                setSelected(null);
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                channelFilter === c.key
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {c.label}
            </button>
          ))}
        </nav>
      </header>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load releases: {error.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_28rem]">
        <div className="space-y-3">
          {isLoading && (
            <p className="text-sm text-gray-500">Loading releases…</p>
          )}
          {!isLoading && releases && releases.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
              No releases on this channel yet. Tag and push from agent/proxy to publish.
            </div>
          )}
          {releases?.map((r) => (
            <ReleaseCard
              key={`${r.channel}:${r.version}`}
              release={r}
              onSelect={setSelected}
              selected={selected && selected.channel === r.channel && selected.version === r.version}
            />
          ))}
        </div>
        {selected && (
          <ReleaseDetail
            release={selected}
            onDelete={() => {
              if (
                window.confirm(
                  `Delete dev release ${selected.version}? This removes the row and every R2 artifact.`,
                )
              ) {
                deleteMutation.mutate(selected);
              }
            }}
            deleting={deleteMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}
